import { LearningPath, LearningPathStandard, PathAssignment } from './types';
import { mockStandards } from './mockStandards';
import { mockTeachers } from './mockTeachers';
import { mockClasses } from './mockClasses';
import { mockStudents } from './mockStudents';

// Helper function to generate random standards for a learning path
const generateRandomStandards = (count: number, grade: string): LearningPathStandard[] => {
  // Filter standards by grade
  const gradeStandards = mockStandards.filter(standard => standard.grade === grade);
  
  // If no standards match the grade, use any standards
  const standardsPool = gradeStandards.length > 0 ? gradeStandards : mockStandards;
  
  // Limit to available standards or requested count, whichever is smaller
  const actualCount = Math.min(count, standardsPool.length);
  
  // Create an array of random standards
  const selectedStandards: LearningPathStandard[] = [];
  
  // Create a copy of the pool to avoid duplicates
  const availableStandards = [...standardsPool];
  
  for (let i = 0; i < actualCount; i++) {
    if (availableStandards.length === 0) break;
    
    // Pick a random standard from the remaining available standards
    const randomIndex = Math.floor(Math.random() * availableStandards.length);
    const standard = availableStandards[randomIndex];
    
    // Remove the selected standard from the available pool
    availableStandards.splice(randomIndex, 1);
    
    // Add the standard to the selected standards with a sequence order
    selectedStandards.push({
      standardId: standard.id,
      sequenceOrder: i + 1
    });
  }
  
  return selectedStandards;
};

// Generate assignments for paths
const generateAssignments = (pathId: string): PathAssignment[] => {
  const assignments: PathAssignment[] = [];
  
  // Randomly decide if this path has assignments
  if (Math.random() > 0.3) {
    const assignmentCount = Math.floor(Math.random() * 3) + 1; // 1-3 assignments
    
    for (let i = 0; i < assignmentCount; i++) {
      // Randomly choose assignment type
      const assignmentTypes: ('Class' | 'Group' | 'Student')[] = ['Class', 'Group', 'Student'];
      const assignedToType = assignmentTypes[Math.floor(Math.random() * assignmentTypes.length)];
      
      // Randomly choose a teacher to be the assigner
      const teacherIndex = Math.floor(Math.random() * mockTeachers.length);
      const assignedBy = mockTeachers[teacherIndex].id;
      
      // Choose assignment target based on type
      let assignedToId = '';
      if (assignedToType === 'Class') {
        const classIndex = Math.floor(Math.random() * mockClasses.length);
        assignedToId = mockClasses[classIndex].id;
      } else if (assignedToType === 'Student') {
        const studentIndex = Math.floor(Math.random() * mockStudents.length);
        assignedToId = mockStudents[studentIndex].id;
      } else {
        // For simplicity, we'll use studentId for groups too since we don't have a groups mock
        const studentIndex = Math.floor(Math.random() * mockStudents.length);
        assignedToId = `group-${studentIndex}`;
      }
      
      // Generate random dates
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - Math.floor(Math.random() * 30)); // Within last 30 days
      
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + Math.floor(Math.random() * 60) + 30); // 30-90 days after start
      
      assignments.push({
        id: `assignment-${pathId}-${i}`,
        pathId,
        assignedBy,
        assignedToType,
        assignedToId,
        isPriority: Math.random() > 0.7, // 30% chance of being priority
        restrictProgression: Math.random() > 0.7, // 30% chance of restricting progression
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
    }
  }
  
  return assignments;
};

// Generate mock learning paths
export const mockLearningPaths: LearningPath[] = [
  // Teacher created paths
  {
    id: 'path-001',
    name: 'Comprehensive Fraction Basics and Advanced Operations for Mathematical Proficiency',
    description: 'Learn fundamental fraction concepts and operations including addition, subtraction, multiplication, and division of fractions with different denominators',
    creatorId: mockTeachers[0].id,
    isPreset: false,
    standards: generateRandomStandards(3, '5'),
    assignedTo: generateAssignments('path-001'),
    createdAt: '2023-02-15T10:00:00Z',
    updatedAt: '2023-02-15T10:00:00Z',
    grade: '5',
    difficulty: 'Medium'
  },
  {
    id: 'path-002',
    name: 'Mastering Decimals',
    description: 'Master working with decimal numbers',
    creatorId: mockTeachers[0].id,
    isPreset: false,
    standards: generateRandomStandards(3, '4'),
    assignedTo: generateAssignments('path-002'),
    createdAt: '2023-03-10T14:30:00Z',
    updatedAt: '2023-03-10T14:30:00Z',
    grade: '4',
    difficulty: 'Easy'
  },
  {
    id: 'path-003',
    name: 'Geometry Fundamentals',
    description: 'Introduction to basic geometry concepts',
    creatorId: mockTeachers[0].id,
    isPreset: false,
    standards: generateRandomStandards(2, '6'),
    assignedTo: generateAssignments('path-003'),
    createdAt: '2023-01-22T09:15:00Z',
    updatedAt: '2023-01-22T09:15:00Z',
    grade: '6',
    difficulty: 'Easy'
  },
  {
    id: 'path-004',
    name: 'Addition and Subtraction',
    description: 'Mastering addition and subtraction operations',
    creatorId: mockTeachers[1].id,
    isPreset: false,
    standards: generateRandomStandards(3, '2'),
    assignedTo: generateAssignments('path-004'),
    createdAt: '2023-04-05T11:20:00Z',
    updatedAt: '2023-04-05T11:20:00Z',
    grade: '2',
    difficulty: 'Easy'
  },
  {
    id: 'path-005',
    name: 'Multiplication Tables',
    description: 'Learn and practice multiplication tables',
    creatorId: mockTeachers[1].id,
    isPreset: false,
    standards: generateRandomStandards(2, '3'),
    assignedTo: generateAssignments('path-005'),
    createdAt: '2023-05-18T13:45:00Z',
    updatedAt: '2023-05-18T13:45:00Z',
    grade: '3',
    difficulty: 'Medium'
  },
  {
    id: 'path-006',
    name: 'Division Mastery',
    description: 'Master division concepts and operations',
    creatorId: mockTeachers[1].id,
    isPreset: false,
    standards: generateRandomStandards(2, '4'),
    assignedTo: generateAssignments('path-006'),
    createdAt: '2023-06-30T15:30:00Z',
    updatedAt: '2023-06-30T15:30:00Z',
    grade: '4',
    difficulty: 'Hard'
  },
  
  // Shared paths from other teachers
  {
    id: 'shared-001',
    name: 'Algebra Basics',
    description: 'Basic algebra concepts for elementary students',
    creatorId: mockTeachers[2].id,
    isPreset: false,
    isShared: true,
    standards: generateRandomStandards(2, '4'),
    assignedTo: generateAssignments('shared-001'),
    createdAt: '2023-04-12T10:00:00Z',
    updatedAt: '2023-04-12T10:00:00Z',
    grade: '4',
    difficulty: 'Medium'
  },
  {
    id: 'shared-002',
    name: 'Number Sense',
    description: 'Building strong number sense fundamentals',
    creatorId: mockTeachers[2].id,
    isPreset: false,
    isShared: true,
    standards: generateRandomStandards(2, '3'),
    assignedTo: generateAssignments('shared-002'),
    createdAt: '2023-03-05T14:30:00Z',
    updatedAt: '2023-03-05T14:30:00Z',
    grade: '3',
    difficulty: 'Easy'
  },
  {
    id: 'shared-003',
    name: 'Advanced Multiplication',
    description: 'Multi-digit multiplication strategies',
    creatorId: mockTeachers[3].id,
    isPreset: false,
    isShared: true,
    standards: generateRandomStandards(2, '5'),
    assignedTo: generateAssignments('shared-003'),
    createdAt: '2023-05-18T09:15:00Z',
    updatedAt: '2023-05-18T09:15:00Z',
    grade: '5',
    difficulty: 'Hard'
  },
  
  // District paths (presets)
  {
    id: 'district-001',
    name: 'District Math Standards Grade 4',
    description: 'Key math standards for grade 4 students district-wide',
    creatorId: 'admin-001', // Admin user
    isPreset: true,
    isDistrict: true, // Mark as district template
    standards: generateRandomStandards(5, '4'),
    assignedTo: generateAssignments('district-001'),
    createdAt: '2023-01-10T10:00:00Z',
    updatedAt: '2023-01-10T10:00:00Z',
    grade: '4',
    difficulty: 'Medium'
  },
  {
    id: 'district-002',
    name: 'District Math Standards Grade 5',
    description: 'Key math standards for grade 5 students district-wide',
    creatorId: 'admin-001', // Admin user
    isPreset: true,
    isDistrict: true, // Mark as district template
    standards: generateRandomStandards(5, '5'),
    assignedTo: generateAssignments('district-002'),
    createdAt: '2023-01-15T14:30:00Z',
    updatedAt: '2023-01-15T14:30:00Z',
    grade: '5',
    difficulty: 'Medium'
  },
  {
    id: 'district-003',
    name: 'District Math Standards Grade 6',
    description: 'Key math standards for grade 6 students district-wide',
    creatorId: 'admin-001', // Admin user
    isPreset: true,
    isDistrict: true, // Mark as district template
    standards: generateRandomStandards(5, '6'),
    assignedTo: generateAssignments('district-003'),
    createdAt: '2023-01-20T09:15:00Z',
    updatedAt: '2023-01-20T09:15:00Z',
    grade: '6',
    difficulty: 'Hard'
  },
  {
    id: 'district-004',
    name: 'District Reading Comprehension Grade 3',
    description: 'Essential reading comprehension skills for grade 3',
    creatorId: 'admin-001', // Admin user
    isPreset: true,
    isDistrict: true, // Mark as district template
    standards: generateRandomStandards(4, '3'),
    assignedTo: generateAssignments('district-004'),
    createdAt: '2023-02-05T11:20:00Z',
    updatedAt: '2023-02-05T11:20:00Z',
    grade: '3',
    difficulty: 'Easy'
  },
  {
    id: 'district-005',
    name: 'District Science Standards Grade 5',
    description: 'Core science concepts for grade 5 students',
    creatorId: 'admin-001', // Admin user
    isPreset: true,
    isDistrict: true, // Mark as district template
    standards: generateRandomStandards(6, '5'),
    assignedTo: generateAssignments('district-005'),
    createdAt: '2023-03-12T14:45:00Z',
    updatedAt: '2023-03-12T14:45:00Z',
    grade: '5',
    difficulty: 'Medium'
  },
  
  // Inventory paths
  {
    id: 'inv-001',
    name: 'Common Core: Fractions Fundamentals',
    description: 'A comprehensive path covering all Common Core fraction standards for grade 5',
    creatorId: 'system',
    isPreset: true,
    standards: generateRandomStandards(4, '5'),
    assignedTo: [],
    createdAt: '2023-01-10T10:00:00Z',
    updatedAt: '2023-01-10T10:00:00Z',
    grade: '5',
    difficulty: 'Medium'
  },
  {
    id: 'inv-002',
    name: 'Decimal Operations Master Path',
    description: 'Complete decimal operations learning sequence for 5th grade',
    creatorId: 'system',
    isPreset: true,
    standards: generateRandomStandards(3, '5'),
    assignedTo: [],
    createdAt: '2023-02-15T14:30:00Z',
    updatedAt: '2023-02-15T14:30:00Z',
    grade: '5',
    difficulty: 'Medium'
  },
  {
    id: 'inv-003',
    name: 'Geometry Exploration',
    description: 'Interactive path for exploring geometry concepts in 5th grade',
    creatorId: 'teacher-special',
    isPreset: true,
    standards: generateRandomStandards(3, '5'),
    assignedTo: [],
    createdAt: '2023-03-22T09:15:00Z',
    updatedAt: '2023-03-22T09:15:00Z',
    grade: '5',
    difficulty: 'Medium'
  },
  {
    id: 'inv-004',
    name: 'Number Patterns and Sequences',
    description: 'Learn to identify, extend, and create number patterns',
    creatorId: 'teacher-special',
    isPreset: true,
    standards: generateRandomStandards(2, '5'),
    assignedTo: [],
    createdAt: '2023-04-05T11:20:00Z',
    updatedAt: '2023-04-05T11:20:00Z',
    grade: '5',
    difficulty: 'Easy'
  },
  {
    id: 'inv-005',
    name: 'Measurement Mastery',
    description: 'Convert between different units of measurement within a system',
    creatorId: 'teacher-special',
    isPreset: true,
    standards: generateRandomStandards(2, '5'),
    assignedTo: [],
    createdAt: '2023-05-18T13:45:00Z',
    updatedAt: '2023-05-18T13:45:00Z',
    grade: '5',
    difficulty: 'Medium'
  },
  {
    id: 'inv-006',
    name: 'Volume Concepts',
    description: 'Understanding and calculating volume of rectangular prisms',
    creatorId: 'system',
    isPreset: true,
    standards: generateRandomStandards(2, '5'),
    assignedTo: [],
    createdAt: '2023-06-30T15:30:00Z',
    updatedAt: '2023-06-30T15:30:00Z',
    grade: '5',
    difficulty: 'Hard'
  }
]; 